import i18next from 'i18next';

import {previewNextYear} from './persistence';

const allowedUrls = [
  'devoted.com',
  'agent.devoted.com',
  'assets.devoted.com',
  'careers.devoted.com',
  'hello.devoted.com',
  'my.devoted.com',
  'staging--devotedhealth.netlify.com',
  'develop--devotedhealth.netlify.com',
  'staging-devoted-health.cdn.prismic.io',
  'devoted-health.cdn.prismic.io',
  'devoted-health-2020.cdn.prismic.io',
  'devoted-health-staging.cdn.prismic.io',
  'devoteddollars.com',
];

// Strip any extraneous https://, http://, www from the beginning of the url
export const sanitizeUrl = (url: string) =>
  url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0];

// Check if given URL is external
export const isExternalUrl = (url: string) =>
  !allowedUrls.some(u => u === sanitizeUrl(url));

// Gather all anchor links on page and give proper attributes if external URL
export const handleExternalLinks = () => {
  // Select anchor tags that start with 'http'
  const allLinks = Array.from(document.querySelectorAll('a[href^="http"]'));

  allLinks.forEach((link: HTMLAnchorElement) => {
    if (!isExternalUrl(link.hostname)) {
      return;
    }
    // Add exit disclaimer for external links
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    link.onclick = () => window.confirm(i18next.t('A11y:exitDisclaimer'));
  });
};

export const activePlanYear = (now: Date) => {
  const offset = showNextYear(now) ? 1 : 0;
  return now.getUTCFullYear() + offset;
};

// Next year's plans can not be enrolled in before 10/15
export const canEnroll = (now: Date, applyingForYear: number) => {
  const thisYear = now.getUTCFullYear();

  // EnrollmentApplications for next year's plans are accepted starting on 10/15 at 7am ET
  if (applyingForYear === thisYear + 1) {
    // ... unless preview mode is enabled, in which case we want you to see the buttons
    if (previewNextYear()) {
      return true;
    }

    const month = now.getUTCMonth();
    if (month > 9) {
      return true;
    } else if (month === 9) {
      const date = now.getUTCDate();
      if (date > 15) {
        return true;
      } else if (date === 15) {
        if (now.getUTCHours() >= 11) {
          return true;
        }
      }
    }

    return false;
  }

  // EnrollmentApplications for this year's plans are accepted at any time
  if (applyingForYear === thisYear) {
    return true;
  }

  // EnrollmentApplications for other plan years are not accepted
  return false;
};

/**
 * Content for the following calendar year is shown after 10/1 at 5am ET or if a session has enabled a preview mode.
 */
export const showNextYear = (now: Date) => {
  const month = now.getUTCMonth();
  if (month > 9) {
    return true;
  } else if (month === 9) {
    const date = now.getUTCDate();
    if (date > 1) {
      return true;
    } else {
      if (now.getUTCHours() >= 9) {
        return true;
      }
    }
  }

  return previewNextYear();
};

export const toTitleCase = (str: string) => {
  return str.toLocaleLowerCase().replace(/(?:^|[\s-/])\w/g, match => {
    return match.toUpperCase();
  });
};

// Format a date time string in a way that can be understood by all operating systems
// iOS requires a slightly different version of the date string (formatted with T instead of a space and adding Z as a timezone indicator)
// https://stackoverflow.com/questions/13363673/javascript-date-is-invalid-on-ios
export const formatDateFromDateTime = (date: string, lang: string) => {
  return new Date(date.replace(' ', 'T') + 'Z').toLocaleString(lang, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// iOS requires a slightly different version of the date string (formatted with T instead of a space)
// https://stackoverflow.com/questions/13363673/javascript-date-is-invalid-on-ios
// https://stackoverflow.com/questions/55655273/date-fns-returns-invalid-date-on-safari
// Expected date string format: '2022-10-15 10:00:00'
export const parseDate = (date: string) => {
  const parsedDate = new Date(date.replace(' ', 'T'));
  return isDateValid(parsedDate) ? parsedDate : undefined;
};

const isDateValid = (date: Date) => {
  return !Number.isNaN(new Date(date).getTime());
};
